import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Badge } from './ui/Badge';
import { OrderDetail } from './orders/OrderDetail';
import { canonicalStatus, statusLabel, statusMeta, TONE_BG } from './orders/workflowConstants';
import { api, extractErrorMessage } from '../api/client';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { DesignImagePreviewModal } from './orders/DesignImagePreviewModal';
import {
  countActiveOrderFilters,
  normalizeOrderListFilters,
  DEFAULT_ORDER_LIST_FILTERS,
  readOrderStatusFromSearch,
  parseOrdersRoute,
  ordersNewPath,
  ordersDetailPath,
} from './orders/orderListFilterUtils';
import { OrderListFiltersPanel } from './orders/OrderListFiltersPanel';
import { pathForMenu } from '../routes/menuPaths';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { useCommercialAccess } from '../permissions/useCommercialAccess';
import { ModuleAccessBanner } from './ui/ModuleAccessBanner';

/** Scroll container of the app layout (see Dashboard.js). */
const MAIN_SCROLL_ID = 'dashboard-main-scroll';
/** How long the order you came back from stays highlighted in the list. */
const RETURN_HIGHLIGHT_MS = 2500;
/** Orders loaded per page (more load automatically while scrolling). */
const PAGE_SIZE = 30;
/** Largest page the server returns; used when refreshing what was already loaded. */
const MAX_PAGE_SIZE = 200;
/** Start loading the next page this far before the end of the list is reached. */
const LOAD_MORE_MARGIN = '800px 0px';

/** Order list filters -> query parameters of GET /api/orders (server applies the same rules). */
function buildOrderListParams(filters) {
  const f = normalizeOrderListFilters(filters);
  const params = {};
  if (f.orderStatus) params.status = f.orderStatus;
  ['search', 'orderNo', 'designNo', 'chalanNo', 'party', 'broker', 'khata', 'concept', 'category', 'beamColor']
    .forEach((key) => {
      if (String(f[key] || '').trim()) params[key] = f[key];
    });
  return params;
}

/** Appends `more` to `list`, skipping orders already present. */
function appendUnique(list, more) {
  const seen = new Set(list.map((o) => String(o.id)));
  return [...list, ...(more || []).filter((o) => !seen.has(String(o.id)))];
}

function readMainScrollTop() {
  const main = document.getElementById(MAIN_SCROLL_ID);
  return main ? main.scrollTop : window.scrollY;
}

function writeMainScrollTop(top) {
  const main = document.getElementById(MAIN_SCROLL_ID);
  if (main) {
    main.scrollTop = top;
  } else {
    window.scrollTo(0, top);
  }
}

/**
 * Orders list. One click on a card opens the unified OrderDetail page which
 * handles create, edit and view in a single surface. The old 3-step add/edit
 * wizard and the separate preview modal are gone — see OrderDetail for that
 * logic.
 */
function Orders() {
  const { canCreate, canEdit } = useModuleAccess('ORDERS');
  const canSeeCommercial = useCommercialAccess();
  const canCreateOrder = canCreate && canSeeCommercial;
  const location = useLocation();
  const navigate = useNavigate();
  /** Orders loaded so far (filtered + sorted newest first by the server). */
  const [orders, setOrders] = useState([]);
  /** Cursor for the next page; null when everything matching the filters is loaded. */
  const [nextCursor, setNextCursor] = useState(null);
  /** Number of orders matching the filters (all pages). */
  const [totalOrders, setTotalOrders] = useState(0);
  /** Concept / category names for the filter dropdowns (from the server). */
  const [facets, setFacets] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const facetsRef = useRef(facets);
  facetsRef.current = facets;
  /** Bumped on every (re)load so answers to outdated requests are ignored. */
  const requestSeqRef = useRef(0);
  /** Filters the current list was loaded with. */
  const loadedParamsKeyRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');

  /** Derived from URL: list | new | detail */
  const ordersRoute = useMemo(
    () => parseOrdersRoute(location.pathname),
    [location.pathname]
  );

  const [pendingSelectDesignId, setPendingSelectDesignId] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteRestrictedOrder, setDeleteRestrictedOrder] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [orderFilters, setOrderFilters] = useState(() => {
    const status = readOrderStatusFromSearch(window.location.search);
    if (status) {
      return { ...DEFAULT_ORDER_LIST_FILTERS, orderStatus: status };
    }
    return { ...DEFAULT_ORDER_LIST_FILTERS };
  });
  const [filtersOpen, setFiltersOpen] = useState(() => Boolean(readOrderStatusFromSearch(window.location.search)));

  /** Scroll position + opened order, saved when leaving the list so Back returns to the same spot. */
  const listReturnRef = useRef(null);
  /** True once the list has loaded successfully; later reloads refresh in the background. */
  const hasLoadedListRef = useRef(false);
  const [highlightOrderId, setHighlightOrderId] = useState(null);

  const rememberListPosition = useCallback((orderId) => {
    listReturnRef.current = { scrollTop: readMainScrollTop(), orderId: orderId ?? null };
  }, []);

  useEffect(() => {
    const status = readOrderStatusFromSearch(location.search);
    if (!status) return;
    setOrderFilters((prev) => ({ ...DEFAULT_ORDER_LIST_FILTERS, ...prev, orderStatus: status }));
    setFiltersOpen(true);
    navigate(pathForMenu('Orders'), { replace: true });
  }, [location.search, navigate]);

  const goToOrdersList = useCallback(() => {
    setPendingSelectDesignId(null);
    navigate({ pathname: '/orders', search: location.search });
  }, [navigate, location.search]);

  const openNewOrder = useCallback(() => {
    rememberListPosition(null);
    navigate(ordersNewPath());
  }, [navigate, rememberListPosition]);

  const openOrderDetail = useCallback((orderId) => {
    rememberListPosition(orderId);
    navigate(ordersDetailPath(orderId));
  }, [navigate, rememberListPosition]);

  useEffect(() => {
    const selectDesignId = location.state?.selectDesignId;
    if (selectDesignId == null || selectDesignId === '') return;
    setPendingSelectDesignId(selectDesignId);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} }
    );
  }, [location.state?.selectDesignId, location.pathname, location.search, navigate]);

  /** Legacy dashboard links that still pass openOrderId in location.state. */
  useEffect(() => {
    const openId = location.state?.openOrderId;
    if (openId == null || openId === '') return;
    const selectDesignId = location.state?.selectDesignId;
    navigate(
      {
        pathname: ordersDetailPath(openId),
        search: location.search,
        state: selectDesignId ? { selectDesignId } : undefined,
      },
      { replace: true }
    );
  }, [location.state?.openOrderId, location.state?.selectDesignId, location.search, navigate]);

  const listParams = useMemo(() => buildOrderListParams(orderFilters), [orderFilters]);
  const listParamsKey = useMemo(() => JSON.stringify(listParams), [listParams]);

  const fetchOrdersPage = useCallback(
    async ({ after, size, includeFacets }) => {
      const { data } = await api.get('/api/orders', {
        params: {
          ...listParams,
          size,
          ...(after ? { after } : {}),
          ...(includeFacets ? { includeFacets: true } : {}),
        },
      });
      return data || {};
    },
    [listParams]
  );

  /**
   * Loads the list for the current filters.
   * `silent` (coming back from an order with the same filters): keep showing the current
   * list and refresh the orders that were already loaded, so the page does not jump.
   */
  const loadOrders = useCallback(async ({ silent = false } = {}) => {
    const seq = ++requestSeqRef.current;
    if (!silent) setLoading(true);
    setPageError('');
    try {
      const wanted = silent ? Math.max(PAGE_SIZE, ordersRef.current.length) : PAGE_SIZE;
      let collected = [];
      let cursor = null;
      let total = 0;
      let pageFacets = null;
      do {
        const size = Math.min(MAX_PAGE_SIZE, wanted - collected.length);
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchOrdersPage({
          after: cursor,
          size,
          includeFacets: !facetsRef.current && !pageFacets,
        });
        if (seq !== requestSeqRef.current) return;
        collected = appendUnique(collected, Array.isArray(data.items) ? data.items : []);
        cursor = data.nextCursor || null;
        total = Number(data.totalElements) || 0;
        if (data.facets) pageFacets = data.facets;
      } while (cursor && collected.length < wanted);
      setOrders(collected);
      setNextCursor(cursor);
      setTotalOrders(total);
      if (pageFacets) setFacets(pageFacets);
      hasLoadedListRef.current = true;
      loadedParamsKeyRef.current = listParamsKey;
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setPageError(extractErrorMessage(e, 'Failed to load orders'));
      if (!silent) {
        setOrders([]);
        setNextCursor(null);
        setTotalOrders(0);
      }
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [fetchOrdersPage, listParamsKey]);

  /** Loads the next page (infinite scroll). */
  const loadMoreOrders = useCallback(async () => {
    if (!nextCursor || loadingMore || loading) return;
    const seq = requestSeqRef.current;
    setLoadingMore(true);
    try {
      const data = await fetchOrdersPage({ after: nextCursor, size: PAGE_SIZE });
      if (seq !== requestSeqRef.current) return;
      setOrders((prev) => appendUnique(prev, Array.isArray(data.items) ? data.items : []));
      setNextCursor(data.nextCursor || null);
      setTotalOrders(Number(data.totalElements) || 0);
    } catch (e) {
      if (seq === requestSeqRef.current) setPageError(extractErrorMessage(e, 'Failed to load more orders'));
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, loading, fetchOrdersPage]);

  useEffect(() => {
    if (ordersRoute.screen !== 'list') return;
    // Same filters as the loaded list -> background refresh; changed filters -> fresh first page.
    loadOrders({ silent: hasLoadedListRef.current && loadedParamsKeyRef.current === listParamsKey });
  }, [ordersRoute.screen, listParamsKey, loadOrders]);

  // Load the next page shortly before the user reaches the end of the list.
  useEffect(() => {
    if (ordersRoute.screen !== 'list' || !nextCursor || loading) return undefined;
    const el = loadMoreSentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreOrders();
      },
      { root: document.getElementById(MAIN_SCROLL_ID), rootMargin: LOAD_MORE_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ordersRoute.screen, nextCursor, loading, loadMoreOrders]);

  // Back from an order: restore the scroll position before the browser paints.
  useLayoutEffect(() => {
    if (ordersRoute.screen !== 'list') return;
    const saved = listReturnRef.current;
    listReturnRef.current = null;
    if (!saved || !hasLoadedListRef.current) return;
    writeMainScrollTop(saved.scrollTop);
    if (saved.orderId != null) setHighlightOrderId(String(saved.orderId));
  }, [ordersRoute.screen]);

  useEffect(() => {
    if (!highlightOrderId) return undefined;
    const t = setTimeout(() => setHighlightOrderId(null), RETURN_HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [highlightOrderId]);

  useEffect(() => {
    if (!pageMessage) return undefined;
    const t = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(t);
  }, [pageMessage]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/orders/${confirmDelete.id}`);
      setOrders((prev) => prev.filter((o) => o.id !== confirmDelete.id));
      setTotalOrders((n) => Math.max(0, n - 1));
      setPageMessage('Order deleted');
      setConfirmDelete(null);
    } catch (e) {
      setConfirmDelete(null);
      setPageError(extractErrorMessage(e, 'Delete failed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const requestOrderDelete = useCallback((order) => {
    setPageError('');
    setPageMessage('');
    if (canonicalStatus(order.workflowStatus) === 'DRAFT') {
      setConfirmDelete(order);
      return;
    }
    setDeleteRestrictedOrder(order);
  }, []);

  const filterBadge = countActiveOrderFilters(orderFilters);

  // Redirect unauthorized users away from /orders/new
  useEffect(() => {
    if (ordersRoute.screen === 'new' && !canCreateOrder) {
      goToOrdersList();
    }
  }, [ordersRoute.screen, canCreateOrder, goToOrdersList]);

  const showOrderDetail = ordersRoute.screen === 'new' || ordersRoute.screen === 'detail';
  const detailOrderId = ordersRoute.screen === 'detail' ? ordersRoute.orderId : null;

  if (showOrderDetail) {
    if (ordersRoute.screen === 'new' && !canCreateOrder) {
      return null;
    }
    return (
      <div className="mx-auto flex min-h-0 flex-1 w-full max-w-[1500px] flex-col">
        <OrderDetail
          key={ordersRoute.screen === 'new' ? 'new' : detailOrderId}
          orderId={detailOrderId}
          initialSelectDesignId={pendingSelectDesignId}
          onBack={goToOrdersList}
          onDraftCreated={(id) => navigate(ordersDetailPath(id), { replace: true })}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-3">
      <ModuleAccessBanner moduleKey="ORDERS" />
      <Card>
        <CardHeader variant="gradient" className="!py-2.5 sm:!py-3">
          <CardListToolbar
            tone="light"
            left={
              <CardTitle className="!text-base !text-white sm:!text-lg">Orders</CardTitle>
            }
            actions={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setFiltersOpen((open) => !open)}
                  className="w-full shrink-0 sm:w-auto"
                >
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                  Filters
                  {filterBadge > 0 && (
                    <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                      {filterBadge}
                    </span>
                  )}
                </Button>
                {canCreateOrder && (
                  <Button type="button" size="sm" onClick={openNewOrder} className="w-full shrink-0 sm:w-auto">
                    <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Order
                  </Button>
                )}
              </>
            }
          />
        </CardHeader>
        <CardBody className="space-y-3 !p-3 sm:!p-4">
          <AlertMessage type="success" message={pageMessage} onClose={() => setPageMessage('')} />
          <AlertMessage type="error" message={pageError} onClose={() => setPageError('')} />

          {filtersOpen ? (
            <OrderListFiltersPanel
              value={orderFilters}
              onApply={setOrderFilters}
              facets={facets}
              showCommercial={canSeeCommercial}
            />
          ) : null}

          {loading ? (
            <p className="py-10 text-center text-sm text-gray-500 sm:text-base">Loading orders…</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {orders.map((o) => (
                <OrderCard
                  key={o.id}
                  order={o}
                  highlighted={highlightOrderId != null && String(o.id) === highlightOrderId}
                  onOpen={() => openOrderDetail(o.id)}
                  onDelete={
                    canEdit && canSeeCommercial
                      ? () => requestOrderDelete(o)
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          {!loading && orders.length > 0 ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <div ref={loadMoreSentinelRef} aria-hidden="true" className="h-px w-full" />
              {nextCursor ? (
                <Button type="button" variant="secondary" size="sm" onClick={loadMoreOrders} disabled={loadingMore}>
                  {loadingMore ? 'Loading more orders…' : 'Load more orders'}
                </Button>
              ) : null}
              <p className="text-xs text-slate-500">
                Showing {orders.length.toLocaleString()} of {totalOrders.toLocaleString()} orders
              </p>
            </div>
          ) : null}

          {!loading && orders.length === 0 && filterBadge === 0 && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-6 py-16 text-center">
              <p className="mx-auto max-w-md text-base text-gray-600 sm:text-lg">No orders yet.</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 sm:text-base">
                {canCreateOrder
                  ? (
                    <>
                      Click <span className="font-semibold">Add Order</span> to create your first one.
                    </>
                  )
                  : 'You can view orders here when they are created.'}
              </p>
            </div>
          )}

          {!loading && orders.length === 0 && filterBadge > 0 && (
            <p className="py-8 text-center text-sm text-slate-500">No orders match your filters.</p>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete order?"
        message={confirmDelete ? `Remove ${confirmDelete.orderCode || 'this order'} permanently?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={Boolean(deleteRestrictedOrder)}
        title="Cannot delete order"
        message={
          deleteRestrictedOrder
            ? buildOrderDeleteBlockedMessage(deleteRestrictedOrder)
            : ''
        }
        confirmText="Got it"
        alertOnly
        onCancel={() => setDeleteRestrictedOrder(null)}
        onConfirm={() => setDeleteRestrictedOrder(null)}
      />
    </div>
  );
}

/** Statuses where the order has entered shop-floor / post-production. */
const PRODUCTION_STARTED_STATUSES = new Set([
  'IN_PRODUCTION',
  'PRODUCTION_DONE',
  'BUTTA_CUTTING',
  'MILL_FINISH',
  'BORDER_CUTTING',
  'HANDWORK',
  'READY_FOR_DISPATCH',
  'IN_STOCK',
  'DISPATCHED',
  'DELIVERED',
]);

function formatCardDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatOrderCode(code) {
  const raw = String(code || '').trim();
  if (!raw) return '—';
  return raw.startsWith('#') ? raw : `#${raw}`;
}

/** Explains why a non-draft order cannot be permanently deleted from the list. */
function buildOrderDeleteBlockedMessage(order) {
  const canonical = canonicalStatus(order.workflowStatus);
  const code = formatOrderCode(order.orderCode || order.id);
  const label = statusLabel(canonical);
  const canCancelViaWorkflow =
    canonical === 'DRAFT' || canonical === 'APPROVED' || canonical === 'PLACE_ORDER';

  if (canonical === 'CANCELLED') {
    return `Order ${code} is already cancelled. Cancelled orders are kept for records and cannot be permanently deleted.`;
  }
  if (canonical === 'DELIVERED' || canonical === 'DISPATCHED') {
    return `Order ${code} is ${label}. Orders at this stage are kept for records and cannot be permanently deleted.`;
  }

  let message = `Only draft orders can be permanently deleted. This order is ${label}.`;
  if (canCancelViaWorkflow) {
    message += ' Open the order and choose More → Cancel order if you need to stop it.';
  } else {
    message += ' This order has already entered production and cannot be removed.';
  }
  return message;
}

/** Clickable card — layout mirrors Designs list cards. */
function OrderCard({ order, onOpen, onDelete, highlighted = false }) {
  const [designPreviewOpen, setDesignPreviewOpen] = useState(false);
  const canonical = canonicalStatus(order.workflowStatus);
  const tone = statusMeta(order.workflowStatus).tone;
  const orderCode = order.orderCode || order.id;
  const chalanNo = String(order.chalanNo || '').trim();
  const showProductionUnit =
    Boolean(String(order.khataName || '').trim()) && PRODUCTION_STARTED_STATUSES.has(canonical);
  const pcsText =
    order.totalPieces != null ? `${Number(order.totalPieces).toLocaleString()} Pcs` : '—';
  const hasDesignImage = Boolean(String(order.designImage || '').trim());

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  const iconBtn =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white shadow-sm transition-colors';

  return (
    <>
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKey}
      className={[
        'group cursor-pointer overflow-hidden rounded-xl border bg-white shadow-md transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
        highlighted ? 'border-primary-300 ring-2 ring-primary-400' : 'border-slate-200 ring-1 ring-slate-900/[0.04]',
      ].join(' ')}
    >
      <div className="flex items-end justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <div className="min-w-0 flex-1 pr-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Order number</p>
          <p className="truncate font-mono text-sm font-bold tabular-nums text-slate-900" title={String(orderCode)}>
            {formatOrderCode(orderCode)}
          </p>
          <p className="mt-0.5 text-xs text-slate-600">
            <span className="font-semibold text-slate-700">Order date</span>{' '}
            <time
              dateTime={order.orderDate ? String(order.orderDate).slice(0, 10) : undefined}
              className="tabular-nums text-slate-800"
            >
              {formatCardDate(order.orderDate)}
            </time>
          </p>
        </div>
        {onDelete ? (
          <button
            type="button"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={`${iconBtn} text-rose-600 hover:bg-rose-50 hover:border-rose-200`}
            aria-label={`Delete order ${orderCode}`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="flex gap-3 p-3">
        <div
          role="presentation"
          onClick={(e) => {
            if (!hasDesignImage) return;
            e.stopPropagation();
            setDesignPreviewOpen(true);
          }}
          className={[
            'relative h-36 w-28 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 sm:h-40 sm:w-32',
            hasDesignImage ? 'cursor-zoom-in' : '',
          ].join(' ')}
          title={hasDesignImage ? 'Preview design' : undefined}
        >
          {hasDesignImage ? (
            <>
              <AuthenticatedImage
                src={order.designImage}
                alt={order.designSampleNo ? `Design ${order.designSampleNo}` : 'Design image'}
                className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                loading="lazy"
                thumbnail
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/50 to-transparent px-2 py-1.5 text-center text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
                View design
              </span>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] leading-tight text-slate-500">
              No image
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-[13px] leading-snug">
          <div>
            <p className="text-[11px] font-medium text-slate-500">Design no.</p>
            <p className="truncate font-mono text-sm font-bold tabular-nums text-slate-900" title={order.designSampleNo || ''}>
              {order.designSampleNo ? `#${order.designSampleNo}` : '—'}
            </p>
          </div>
          <p className="text-xs text-slate-600">
            <span className="font-semibold text-slate-700">Pcs =</span>{' '}
            <span className="text-slate-800">{pcsText}</span>
          </p>
          {chalanNo ? (
            <p className="text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Chalan no. =</span>{' '}
              <span className="text-slate-800">{chalanNo}</span>
            </p>
          ) : null}
          {showProductionUnit ? (
            <p className="text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Production unit =</span>{' '}
              <span className="text-slate-800">{order.khataName}</span>
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 bg-slate-50/60 px-3 py-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-bold ring-1 ${
            TONE_BG[tone] || TONE_BG.slate
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-75" />
          {statusLabel(canonical)}
        </span>
        {order.onHold && (
          <Badge tone="warning" dot>
            On hold
          </Badge>
        )}
        <span className="ml-auto text-[11px] text-slate-500">
          <span className="font-semibold text-slate-600">Status updated</span>{' '}
          <time
            dateTime={order.updatedAt ? String(order.updatedAt).slice(0, 10) : undefined}
            className="tabular-nums text-slate-700"
          >
            {formatCardDate(order.updatedAt)}
          </time>
        </span>
      </div>

    </article>

    <DesignImagePreviewModal
      open={designPreviewOpen}
      onClose={() => setDesignPreviewOpen(false)}
      design={{
        designImage: order.designImage,
        sampleNo: order.designSampleNo,
        conceptName: order.designConceptName,
        categoryName: order.designCategoryName,
        subCategoryName: order.designSubCategoryName,
      }}
    />
    </>
  );
}

export default Orders;
